import { Area } from '../../../../../../shared/helpers/layout-manager/types';
import { DisplayMode, drawMedia } from '../draw-helpers';
import { FrameCacheEntry } from './types';

const MAX_ENTRIES_PER_VIDEO = 2;

interface DrawSlotOptions {
    mode: DisplayMode;
    isMirror: boolean;
    dirtyVideos: ReadonlySet<HTMLVideoElement> | undefined;
}

/**
 * Кэш отрисованных кадров участников: для каждого `<video>` храним последний
 * нарисованный кадр на отдельном холсте под размер его слота. На следующем
 * кадре, если у видео ничего не поменялось, копируем готовое изображение из
 * кэша вместо повторной отрисовки из `<video>` — это сильно дешевле.
 *
 * Запись пересоздаётся в трёх случаях: видео сообщило о новом кадре (через
 * VideoFrameTracker), у него тикнул `currentTime` (страховка для тех видео,
 * которые трекер не отслеживает) или записи для данного слота ещё нет.
 *
 * Одно и то же видео в одном кадре может рисоваться в нескольких слотах
 * разного размера (например, в большом презентере и в маленькой превьюшке), —
 * поэтому ключ кэша учитывает размер слота и mode/mirror. Старые записи
 * вытесняются по FIFO, чтобы не разрастаться при смене раскладок.
 */
export class FrameCache {
    private _cache: Map<HTMLVideoElement, Map<string, FrameCacheEntry>> = new Map();

    public has(video: HTMLVideoElement): boolean {
        return this._cache.has(video);
    }

    /** Вызывается из VideoFrameTracker при удалении video из DOM. */
    public remove(video: HTMLVideoElement): void {
        this._cache.delete(video);
    }

    public destroy(): void {
        this._cache.clear();
    }

    public get size(): number {
        return this._cache.size;
    }

    public drawSlot(
        targetCtx: CanvasRenderingContext2D,
        video: HTMLVideoElement,
        drawArea: Area,
        options: DrawSlotOptions
    ): void {
        if (!video.videoWidth || !video.videoHeight) {
            // Video не готово — мимо кэша, drawer наверху отрисует stub.
            drawMedia(targetCtx, drawArea, video, { mode: options.mode, isMirror: options.isMirror });
            return;
        }

        const key = makeCacheKey(drawArea.width, drawArea.height, options.mode, options.isMirror);
        const entries = this._getOrCreateEntries(video);
        const existing = entries.get(key);

        const isDirty = !options.dirtyVideos || options.dirtyVideos.has(video);
        const timeAdvanced = Boolean(existing && existing.lastCachedTime !== video.currentTime);

        const entry =
            !existing || isDirty || timeAdvanced
                ? this._refresh(entries, key, drawArea.width, drawArea.height, video, options, existing)
                : existing;

        targetCtx.drawImage(entry.canvas, drawArea.x, drawArea.y);
    }

    private _getOrCreateEntries(video: HTMLVideoElement): Map<string, FrameCacheEntry> {
        const existing = this._cache.get(video);
        if (existing) {
            return existing;
        }
        const entries = new Map<string, FrameCacheEntry>();
        this._cache.set(video, entries);
        return entries;
    }

    private _refresh(
        entries: Map<string, FrameCacheEntry>,
        key: string,
        slotWidth: number,
        slotHeight: number,
        video: HTMLVideoElement,
        options: DrawSlotOptions,
        existing: FrameCacheEntry | undefined
    ): FrameCacheEntry {
        const entry = existing ?? this._createEntry(entries, key, slotWidth, slotHeight);
        entry.ctx.clearRect(0, 0, entry.slotWidth, entry.slotHeight);
        drawMedia(entry.ctx, { x: 0, y: 0, width: entry.slotWidth, height: entry.slotHeight }, video, {
            mode: options.mode,
            isMirror: options.isMirror,
        });
        entry.lastCachedTime = video.currentTime;
        return entry;
    }

    private _createEntry(
        entries: Map<string, FrameCacheEntry>,
        key: string,
        slotWidth: number,
        slotHeight: number
    ): FrameCacheEntry {
        // FIFO: Map.keys() возвращает в порядке вставки, первая — самая старая.
        if (entries.size >= MAX_ENTRIES_PER_VIDEO) {
            const oldestKey = entries.keys().next().value;
            if (oldestKey !== undefined) {
                entries.delete(oldestKey);
            }
        }
        const canvas = new OffscreenCanvas(slotWidth, slotHeight);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Failed to get 2d context from OffscreenCanvas');
        }
        const entry: FrameCacheEntry = { canvas, ctx, slotWidth, slotHeight, lastCachedTime: -1 };
        entries.set(key, entry);
        return entry;
    }
}

function makeCacheKey(slotWidth: number, slotHeight: number, mode: DisplayMode, isMirror: boolean): string {
    return `${slotWidth}x${slotHeight}-${mode}-${isMirror ? 'm' : ''}`;
}
